"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { Check } from "lucide-react";
import { buildAuthPanelPalette } from "@growwithai/shared";
import BrandLogo from "@/components/tenant/BrandLogo";
import SafeImage from "@/components/common/SafeImage";
import { useTenant } from "@/components/tenant/TenantProvider";
import { AnimatedCharactersPanel } from "@/components/ui/animated-characters";

// Panel kiri halaman auth (desktop) — 4 varian dari auth_config.panel:
// illustration (default, karakter animasi palet brand), image, gradient, none.
// Config dibaca langsung dari window.__TENANT__ karena useTenantAuthConfig()
// hanya meneruskan field copy (panel tidak ikut) — TenantProvider milik
// workstream lain, jangan diubah dari sini.

export type AuthPanelStyle = "illustration" | "image" | "gradient" | "none";
export interface AuthPanelConfig {
  style: AuthPanelStyle;
  image_url?: string;
  overlay_heading?: string;
  overlay_points?: string[];
}
const DEFAULT_PANEL: AuthPanelConfig = {
  style: "illustration"
};
const PANEL_STYLES: readonly AuthPanelStyle[] = ["illustration", "image", "gradient", "none"];
function isHttpUrl(v: unknown): v is string {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

/** Parse ringan auth_config.panel dari window.__TENANT__ — toleran bentuk apa pun. */
export function readAuthPanelConfig(): AuthPanelConfig {
  if (typeof window === "undefined" || !window.__TENANT__) return DEFAULT_PANEL;
  try {
    const raw = window.__TENANT__ as {
      auth_config?: unknown;
    };
    const auth = raw.auth_config;
    if (!auth || typeof auth !== "object" || Array.isArray(auth)) return DEFAULT_PANEL;
    const panel = (auth as {
      panel?: unknown;
    }).panel;
    if (!panel || typeof panel !== "object" || Array.isArray(panel)) return DEFAULT_PANEL;
    const p = panel as Record<string, unknown>;
    const style = PANEL_STYLES.includes(p.style as AuthPanelStyle) ? p.style as AuthPanelStyle : "illustration";
    const points = Array.isArray(p.overlay_points) ? p.overlay_points.filter((s): s is string => typeof s === "string" && s.trim() !== "").slice(0, 4) : undefined;
    return {
      style,
      image_url: isHttpUrl(p.image_url) ? p.image_url : undefined,
      overlay_heading: typeof p.overlay_heading === "string" && p.overlay_heading.trim() !== "" ? p.overlay_heading : undefined,
      overlay_points: points && points.length > 0 ? points : undefined
    };
  } catch {
    return DEFAULT_PANEL;
  }
}

/** Config panel auth — dibaca sekali secara sinkron (window.__TENANT__ hadir pra-hydration). */
export function useAuthPanelConfig() {
  const [config] = useState(readAuthPanelConfig);
  return config;
}
function OverlayContent(t0) {
  const $ = _c(7);
  const {
    heading,
    points
  } = t0;
  if (!heading && (!points || points.length === 0)) {
    return null;
  }
  let t1;
  if ($[0] !== heading) {
    t1 = heading && <h2 className="text-3xl font-bold leading-tight tracking-tight text-white">{heading}</h2>;
    $[0] = heading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== points) {
    t2 = points && points.length > 0 && <ul className="mt-5 space-y-3">{points.map(_temp)}</ul>;
    $[2] = points;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="relative z-20 max-w-md">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(point) {
  return <li key={point} className="flex items-start gap-2.5 text-[15px] text-white/90"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15"><Check className="h-3 w-3 text-white" strokeWidth={3} /></span>{point}</li>;
}
function PanelFooter() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative z-20 mt-10 flex items-center gap-8 text-sm text-white/60"><Link href="/privacy" className="transition-colors hover:text-white">Kebijakan Privasi</Link><Link href="/terms" className="transition-colors hover:text-white">{"Syarat & Ketentuan"}</Link><Link href="/kontak" className="transition-colors hover:text-white">Kontak</Link></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export interface AuthPanelProps {
  /** Varian panel dari auth_config.panel; absen = illustration. */
  variant?: AuthPanelConfig | null;
  isTyping?: boolean;
  password?: string;
  showPassword?: boolean;
}
export default function AuthPanel(t0) {
  const $ = _c(21);
  const {
    variant,
    isTyping: t1,
    password: t2,
    showPassword: t3
  } = t0;
  const isTyping = t1 === undefined ? false : t1;
  const password = t2 === undefined ? "" : t2;
  const showPassword = t3 === undefined ? false : t3;
  const branding = useTenant();
  const style = variant?.style ?? "illustration";
  const rawAuthBg = branding.theme?.auth_background_url;
  let t4;
  if ($[0] !== rawAuthBg) {
    t4 = isHttpUrl(rawAuthBg) ? rawAuthBg : undefined;
    $[0] = rawAuthBg;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const authBg = t4;
  if (style === "none") {
    return null;
  }
  if (!authBg && (style === "illustration" || style === "image" && !variant?.image_url)) {
    let t5;
    if ($[2] !== branding.colors.primary) {
      t5 = buildAuthPanelPalette(branding.colors.primary);
      $[2] = branding.colors.primary;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== isTyping || $[5] !== password || $[6] !== showPassword || $[7] !== t5) {
      t6 = <AnimatedCharactersPanel palette={t5} isTyping={isTyping} password={password} showPassword={showPassword} />;
      $[4] = isTyping;
      $[5] = password;
      $[6] = showPassword;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  const bgImageUrl = authBg ?? (style === "image" ? variant?.image_url : undefined);
  let t5;
  if ($[9] !== bgImageUrl) {
    t5 = bgImageUrl ? <><SafeImage src={bgImageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" /><div aria-hidden={true} className="absolute inset-0" style={{
        background: "color-mix(in srgb, var(--brand-primary) 30%, rgba(0,0,0,0.55))"
      }} /></> : <div aria-hidden={true} className="absolute inset-0" style={{
      background: "color-mix(in srgb, var(--brand-primary) 88%, black)"
    }} />;
    $[9] = bgImageUrl;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="relative z-20"><div className="inline-flex rounded-2xl bg-white/10 px-3 py-2 backdrop-blur-sm"><BrandLogo width={140} height={36} priority={true} className="h-8 w-auto" /></div></div>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = variant?.overlay_heading;
  const t8 = variant?.overlay_points;
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <OverlayContent heading={t7} points={t8} />;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <PanelFooter />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t9) {
    t11 = <div className="relative z-20">{t9}{t10}</div>;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t5) {
    t12 = <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 text-white">{t5}{t6}{t11}</div>;
    $[18] = t11;
    $[19] = t5;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}